import { useQueryClient } from '@tanstack/react-query';
import { App, Button, Space } from 'antd';
import { type MessagePayload, onMessage } from 'firebase/messaging';
import type React from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useRemoveUserFcmToken, useReplaceUserFcmToken } from '@/common/query/fcm.hooks';
import { QueryKeys } from '@/common/queryKeys';
import {
  FCM_SOURCE_EXPRESS_NOT_TAKEN,
  getFCMToken,
  getMessagingInstance,
  getNotificationPermissionStatus,
  isNotificationSupported,
  registerServiceWorker,
  requestNotificationPermission,
} from '@/services/fcm/fcmService';
import type { NotificationPermissionStatus } from '@/services/fcm/types';
import { formatError } from '@/utils/formatError';
import { useAuth } from './AuthFirebaseProvider';

const FCM_FOREGROUND_MESSAGE_TYPE = 'FCM_FOREGROUND_PAYLOAD';
const NOTIFICATION_CLICK_NAVIGATE_TYPE = 'NOTIFICATION_CLICK_NAVIGATE';

const SUPPRESS_ANTD_AFTER_NOTIFICATION_CLICK_MS = 5000;

let globalFcmMessageHandler: ((payload: MessagePayload) => void) | null = null;
let globalNotificationClickNavigateHandler: ((url: string) => void) | null = null;
let swMessageListenerRegistered = false;
const shownAntdKeys = new Set<string>();
const suppressedAntdOrderIdsFromNotificationClick = new Set<string>();

function registerSwMessageListenerOnce(): void {
  if (swMessageListenerRegistered || typeof navigator === 'undefined' || !navigator.serviceWorker) {
    return;
  }
  swMessageListenerRegistered = true;
  navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
    if (event.data?.type === NOTIFICATION_CLICK_NAVIGATE_TYPE && event.data?.url) {
      const orderId =
        event.data.orderId ??
        (() => {
          try {
            return new URL(event.data.url as string).searchParams.get('orderId');
          } catch {
            return null;
          }
        })();
      if (orderId) {
        suppressedAntdOrderIdsFromNotificationClick.add(String(orderId));
        setTimeout(() => {
          suppressedAntdOrderIdsFromNotificationClick.delete(String(orderId));
        }, SUPPRESS_ANTD_AFTER_NOTIFICATION_CLICK_MS);
      }
      globalNotificationClickNavigateHandler?.(event.data.url as string);
      return;
    }
    if (
      event.data?.type !== FCM_FOREGROUND_MESSAGE_TYPE ||
      !event.data?.payload ||
      !globalFcmMessageHandler
    ) {
      return;
    }
    const payload = event.data.payload as MessagePayload;
    globalFcmMessageHandler(payload);
  });
}

interface FCMContextValue {
  permissionStatus: NotificationPermissionStatus;
  fcmToken: string | null;
  isInitialized: boolean;
  isInitializing: boolean;
  requestPermission: () => Promise<void>;
  refreshToken: () => Promise<void>;
  removeFCMToken: () => Promise<void>;
}

const FCMContext = createContext<FCMContextValue | undefined>(undefined);

let globalRemoveFCMToken: (() => Promise<void>) | null = null;

export const removeFCMTokenGlobal = async () => {
  if (globalRemoveFCMToken) {
    await globalRemoveFCMToken();
  }
};

export const FCMProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { message: messageApi, notification: notificationApi } = App.useApp();
  const { userDataParams, status: authStatus } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { mutateAsync: replaceToken } = useReplaceUserFcmToken();
  const { mutateAsync: removeToken } = useRemoveUserFcmToken();

  const [permissionStatus, setPermissionStatus] = useState<NotificationPermissionStatus>('default');
  const [fcmToken, setFcmToken] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [swRegistration, setSwRegistration] = useState<ServiceWorkerRegistration | null>(null);
  const [isInitializing, setIsInitializing] = useState(false);
  const processedMessagesRef = useRef<Set<string>>(new Set());

  const updateBadge = useCallback((count: number) => {
    if ('setAppBadge' in navigator && count > 0) {
      navigator.setAppBadge(count).catch(() => {});
    } else if ('clearAppBadge' in navigator && count === 0) {
      navigator.clearAppBadge().catch(() => {});
    }
  }, []);

  const getTargetPath = useCallback(
    (payload: MessagePayload, parsedData?: Record<string, string>): string => {
      const dataSource = parsedData || (payload.data as Record<string, string> | undefined);

      const orderId = dataSource?.orderId ? Number(dataSource.orderId) : null;
      if (!orderId || Number.isNaN(orderId)) {
        return '/';
      }

      const params = new URLSearchParams();
      params.set('orderId', String(orderId));

      if (dataSource?.departmentId) {
        const departmentId = Number(dataSource.departmentId);
        if (!Number.isNaN(departmentId)) {
          params.set('departmentId', String(departmentId));
        }
      }

      const statusName = dataSource?.statusName;
      if (statusName && typeof statusName === 'string') {
        params.set('statusName', statusName);
      }

      return `/?${params.toString()}`;
    },
    []
  );

  const invalidateOrderQueries = useCallback(() => {
    queryClient.invalidateQueries({
      predicate: (query) =>
        query.queryKey[0] === QueryKeys.GET_ORDERS || query.queryKey[0] === QueryKeys.GET_STATUSES,
    });
  }, [queryClient]);

  const parsePayloadData = useCallback((payload: MessagePayload) => {
    const data = payload.data as Record<string, string> | undefined;
    let parsedData: Record<string, string> = {};

    if (data) {
      parsedData = { ...data } as Record<string, string>;
      try {
        const nestedData = JSON.parse(data.data) as Record<string, string>;
        parsedData = { ...parsedData, ...nestedData };
      } catch (_e) {}
    }

    return { data, parsedData };
  }, []);

  const checkMessageDuplication = useCallback((messageId: string): boolean => {
    if (processedMessagesRef.current.has(messageId)) {
      return true;
    }
    processedMessagesRef.current.add(messageId);
    setTimeout(() => {
      processedMessagesRef.current.delete(messageId);
    }, 60000);
    return false;
  }, []);

  const shouldShowAntdNotification = useCallback(
    (
      isExpressNotTaken: boolean,
      fromServiceWorker: boolean,
      hasNotificationContent: boolean
    ): boolean => {
      if (fromServiceWorker && (isExpressNotTaken || hasNotificationContent)) {
        return true;
      }
      if (isExpressNotTaken && !fromServiceWorker) {
        return true;
      }
      return false;
    },
    []
  );

  const showAntdNotification = useCallback(
    (
      payload: MessagePayload,
      parsedData: Record<string, string>,
      data: Record<string, string> | undefined,
      notificationKey: string
    ) => {
      if (shownAntdKeys.has(notificationKey)) {
        return;
      }
      shownAntdKeys.add(notificationKey);
      setTimeout(() => {
        shownAntdKeys.delete(notificationKey);
      }, 60000);

      const shortCode = parsedData.shortCode || data?.shortCode || '';
      const title =
        payload.notification?.title ?? (shortCode ? `New DTK EXPRESS Order` : 'New Notification');
      const body = payload.notification?.body ?? (shortCode ? `Order ${shortCode} received.` : '');

      const handleOpenOrder = () => {
        notificationApi.destroy();
        navigate(getTargetPath(payload, parsedData), { replace: true });
      };

      const btn = (
        <Space size="small">
          <Button type="primary" size="small" onClick={handleOpenOrder}>
            Open order
          </Button>
          <Button type="link" size="small" onClick={() => notificationApi.destroy()}>
            Clear all
          </Button>
        </Space>
      );

      notificationApi.info({
        message: title,
        description: body,
        placement: 'topRight',
        btn,
      });
    },
    [getTargetPath, navigate, notificationApi]
  );

  const handleForegroundMessage = useCallback(
    (payload: MessagePayload, fromServiceWorker = false) => {
      const { data, parsedData } = parsePayloadData(payload);

      const source = parsedData.source || data?.source;
      const messageId =
        parsedData.fcmMessageId ||
        data?.fcmMessageId ||
        parsedData.messageId ||
        data?.messageId ||
        Date.now().toString();
      const orderId = parsedData.orderId || data?.orderId || '';

      const isExpressNotTaken = source === FCM_SOURCE_EXPRESS_NOT_TAKEN;

      if (isExpressNotTaken && !fromServiceWorker) {
        const notificationKey = `antd_${orderId}_${source}`;
        if (shownAntdKeys.has(notificationKey)) {
          return;
        }
        shownAntdKeys.add(notificationKey);
        setTimeout(() => {
          shownAntdKeys.delete(notificationKey);
        }, 60000);
      }

      invalidateOrderQueries();
      updateBadge(1);

      const hasNotificationContent =
        Boolean(payload.notification) ||
        Boolean(parsedData.title || data?.title || parsedData.body || data?.body);

      if (
        !shouldShowAntdNotification(isExpressNotTaken, fromServiceWorker, hasNotificationContent)
      ) {
        return;
      }

      if (orderId && suppressedAntdOrderIdsFromNotificationClick.has(orderId)) {
        suppressedAntdOrderIdsFromNotificationClick.delete(orderId);
        return;
      }

      if (checkMessageDuplication(messageId)) {
        return;
      }

      showAntdNotification(payload, parsedData, data, `antd_${orderId}_${source}`);
    },
    [
      parsePayloadData,
      checkMessageDuplication,
      shouldShowAntdNotification,
      showAntdNotification,
      invalidateOrderQueries,
      updateBadge,
    ]
  );

  const handleTokenSave = useCallback(
    async (token: string) => {
      setFcmToken(token);
      localStorage.setItem('fcm_token', token);

      try {
        await replaceToken(token);
      } catch (saveError) {
        const errorMessage = formatError(saveError, 'Save FCM Token');
        messageApi.error(`Failed to save notification token: ${errorMessage}`);
        localStorage.removeItem('fcm_token');
        setFcmToken(null);
        throw saveError;
      }
    },
    [replaceToken, messageApi]
  );

  const obtainAndSaveToken = useCallback(
    async (registration?: ServiceWorkerRegistration) => {
      try {
        const token = await getFCMToken(registration || swRegistration || undefined);

        if (token) {
          await handleTokenSave(token);
          setPermissionStatus('granted');
        } else {
          setPermissionStatus('no-token');
          messageApi.error('Failed to obtain FCM token');
        }
      } catch (error) {
        const errorMessage = formatError(error, 'Obtain FCM Token');
        messageApi.error(`Failed to obtain notification token: ${errorMessage}`);
        setPermissionStatus('no-token');
      }
    },
    [handleTokenSave, swRegistration, messageApi]
  );

  const requestPermission = useCallback(async () => {
    setIsInitializing(true);
    try {
      const result = await requestNotificationPermission();

      if (result === 'granted') {
        await obtainAndSaveToken(swRegistration || undefined);
      } else {
        setPermissionStatus(result);
      }
    } catch (error) {
      const errorMessage = formatError(error, 'Request Notification Permission');
      messageApi.error(`Failed to request notification permission: ${errorMessage}`);
      setPermissionStatus('denied');
    } finally {
      setIsInitializing(false);
    }
  }, [obtainAndSaveToken, swRegistration, messageApi]);

  const refreshToken = useCallback(async () => {
    if (permissionStatus === 'granted') {
      await obtainAndSaveToken(swRegistration || undefined);
    }
  }, [permissionStatus, obtainAndSaveToken, swRegistration]);

  const removeFCMToken = useCallback(async () => {
    const currentToken = localStorage.getItem('fcm_token');
    if (currentToken) {
      try {
        await removeToken(currentToken);
        localStorage.removeItem('fcm_token');
        setFcmToken(null);
      } catch (error) {
        const errorMessage = formatError(error, 'Remove FCM Token');
        messageApi.error(`Failed to remove notification token: ${errorMessage}`);
      }
    }
  }, [removeToken, messageApi]);

  useEffect(() => {
    globalRemoveFCMToken = removeFCMToken;
    return () => {
      globalRemoveFCMToken = null;
    };
  }, [removeFCMToken]);

  useEffect(() => {
    registerSwMessageListenerOnce();
    globalNotificationClickNavigateHandler = (url: string) => {
      try {
        const parsed = new URL(url);
        if (parsed.origin === window.location.origin) {
          navigate(parsed.pathname + parsed.search);
        }
      } catch {
        // ignore invalid URL
      }
    };
    return () => {
      globalNotificationClickNavigateHandler = null;
    };
  }, [navigate]);

  const handleGrantedPermission = useCallback(
    async (registration?: ServiceWorkerRegistration) => {
      const token = await getFCMToken(registration);

      if (token) {
        try {
          await handleTokenSave(token);
          setPermissionStatus('granted');
        } catch {
          setPermissionStatus('no-token');
        }
      } else {
        setPermissionStatus('no-token');
      }
    },
    [handleTokenSave]
  );

  useEffect(() => {
    if (authStatus !== 'in' || !userDataParams || isInitialized) {
      return;
    }

    const initialize = async () => {
      setIsInitializing(true);

      try {
        if (!isNotificationSupported()) {
          setPermissionStatus('unsupported');
          setIsInitialized(true);
          return;
        }

        const registration = await registerServiceWorker();
        if (registration) {
          setSwRegistration(registration);
        }

        const currentStatus = getNotificationPermissionStatus();

        if (currentStatus === 'granted') {
          await handleGrantedPermission(registration || undefined);
        } else {
          setPermissionStatus(currentStatus);
        }

        setIsInitialized(true);
      } catch (error) {
        const errorMessage = formatError(error, 'FCM Initialization');
        messageApi.error(`Failed to initialize notifications: ${errorMessage}`);
        setPermissionStatus('no-token');
        setIsInitialized(true);
      } finally {
        setIsInitializing(false);
      }
    };

    initialize();
  }, [authStatus, userDataParams, isInitialized, handleGrantedPermission, messageApi]);

  useEffect(() => {
    if (permissionStatus !== 'granted') {
      return;
    }

    const messaging = getMessagingInstance();
    if (!messaging) {
      return;
    }

    const unsubscribe = onMessage(messaging, () => {});

    registerSwMessageListenerOnce();

    globalFcmMessageHandler = (payload: MessagePayload) => {
      const data = payload.data as Record<string, string> | undefined;
      const orderId = data?.orderId ?? '';
      const source = data?.source ?? '';
      const key = `antd_${orderId}_${source}`;
      if (shownAntdKeys.has(key)) {
        return;
      }
      shownAntdKeys.add(key);
      setTimeout(() => {
        shownAntdKeys.delete(key);
      }, 60000);
      handleForegroundMessage(payload, true);
    };

    return () => {
      unsubscribe();
      globalFcmMessageHandler = null;
    };
  }, [permissionStatus, handleForegroundMessage]);

  const contextValue = useMemo<FCMContextValue>(
    () => ({
      permissionStatus,
      fcmToken,
      isInitialized,
      isInitializing,
      requestPermission,
      refreshToken,
      removeFCMToken,
    }),
    [
      permissionStatus,
      fcmToken,
      isInitialized,
      isInitializing,
      requestPermission,
      refreshToken,
      removeFCMToken,
    ]
  );

  return <FCMContext.Provider value={contextValue}>{children}</FCMContext.Provider>;
};

export const useFCM = (): FCMContextValue => {
  const context = useContext(FCMContext);

  if (!context) {
    throw new Error('useFCM must be used within FCMProvider');
  }

  return context;
};
